import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { decisionRef } from '@/lib/decisionRef';
import { useLocalization } from '@/hooks/useLocalization';

interface Props { onNavigate: (view: string) => void }

interface Row { id: string; ref: string; title: string; action: string; note: string | null; at: string }

/** Evidence trail: every recorded decision event, newest first, linked back to its decision. */
const TraceabilityView = ({ onNavigate }: Props) => {
  const { tx, t } = useLocalization();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['traceability'],
    staleTime: 30_000,
    queryFn: async (): Promise<Row[]> => {
      const { data: logs, error } = await supabase
        .from('decision_audit_logs').select('id,decision_id,action,notes,created_at')
        .order('created_at', { ascending: false }).limit(100);
      if (error) throw new Error(error.message);
      const ids = [...new Set((logs ?? []).map((l) => l.decision_id))];
      const { data: decs, error: dErr } = ids.length
        ? await supabase.from('decisions').select('id,title,created_at').in('id', ids)
        : { data: [], error: null };
      if (dErr) throw new Error(dErr.message);
      const byId = new Map((decs ?? []).map((d) => [d.id, d]));
      return (logs ?? []).map((l) => {
        const d = byId.get(l.decision_id);
        return { id: l.id, ref: decisionRef(l.decision_id, d?.created_at ?? null), title: d?.title ?? 'Decision', action: l.action, note: l.notes, at: l.created_at };
      });
    },
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <p role="alert" className="text-sm text-muted-foreground">{t('The audit trail could not be loaded.')}</p>;
  const rows = data ?? [];
  const week = rows.filter((r) => Date.now() - new Date(r.at).getTime() < 7 * 86400_000).length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {rows.length === 0 ? t('No decision events recorded yet.') : t('{0} event{1} recorded in the last 7 days. Each links back to the decision, so you can trace evidence to decision to action.', { 0: week, 1: week === 1 ? '' : 's' })}
      </p>
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <caption className="sr-only">{t('Decision events, newest first')}</caption>
            <thead>
              <tr className="border-b border-border text-start font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="px-4 py-3 text-start font-normal">{t('Decision ID')}</th>
                <th scope="col" className="px-4 py-3 text-start font-normal">{t('Decision')}</th>
                <th scope="col" className="px-4 py-3 text-start font-normal">{t('Event')}</th>
                <th scope="col" className="px-4 py-3 text-start font-normal">{t('Note')}</th>
                <th scope="col" className="px-4 py-3 text-start font-normal">{t('Timestamp')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => onNavigate('decisions')} className="whitespace-nowrap font-mono text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">{r.ref}</button>
                  </td>
                  <td className="px-4 py-3 font-semibold">{tx(r.title)}</td>
                  <td className="px-4 py-3 capitalize">{r.action.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.note}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{format(new Date(r.at), 'dd MMM HH:mm')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TraceabilityView;
